import { Link, NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  ShoppingBag,
  Users,
  Package,
  Boxes,
  Layers,
  Warehouse,
  PackagePlus,
  Building2,
  Wallet,
  ReceiptText,
  Store,
  Settings,
  BarChart3,
  MessageSquare,
  UserSearch,
  Target,
  HandCoins,
  Hammer,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { env } from '@/lib/env'
import { usePermissions } from '@/lib/hooks/use-permissions'
import { useSidebarCollapsed } from '@/lib/hooks/use-sidebar'
import { PERMISSIONS, REPORTS_HUB_PERMISSIONS } from '@/lib/permissions'
import { COD_REDUCTION_MODULE_PERMISSIONS } from '@/features/cod-reductions/cod-reduction-utils'
import { useCodReductionMyTurnCount } from '@/features/cod-reductions/use-cod-reductions-list'
import { PIECEWORK_MODULE_PERMISSIONS } from '@/features/piecework/piecework-utils'
import type { PermissionName } from '@/types/db'

type NavItem = {
  to: string
  label: string
  icon: typeof LayoutDashboard
  end?: boolean
  /**
   * Required permission to see this item. If any of the listed permissions
   * is granted, the item is shown. Omit to always show.
   */
  requiresAny?: ReadonlyArray<PermissionName>
  /**
   * Badge "chờ tôi xử lý" (số đỏ). Mục cũng HIỆN khi số > 0 dù thiếu quyền ở
   * requiresAny — người duyệt theo người/vị trí (vd Sale phụ trách duyệt hạ COD)
   * không có quyền module nhưng vẫn phải thấy việc chờ mình.
   */
  inboxBadge?: 'cod-reductions'
}

/** Nhóm điều hướng — gom các mục cùng nghiệp vụ dưới 1 tiêu đề mảnh. */
type NavGroup = {
  /** Tiêu đề nhóm; null = nhóm không tiêu đề (mục lẻ đầu sidebar). */
  title: string | null
  items: NavItem[]
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: null,
    items: [{ to: '/', label: 'Tổng quan', icon: LayoutDashboard, end: true }],
  },
  {
    title: 'Bán hàng',
    items: [
      {
        to: '/orders',
        label: 'Đơn hàng',
        icon: ShoppingBag,
        requiresAny: [PERMISSIONS.ORDER_VIEW],
      },
      {
        to: '/cod-reductions',
        label: 'Hạ COD',
        icon: HandCoins,
        requiresAny: COD_REDUCTION_MODULE_PERMISSIONS,
        inboxBadge: 'cod-reductions',
      },
      {
        to: '/customers',
        label: 'Khách hàng',
        icon: Users,
        requiresAny: [PERMISSIONS.CUSTOMER_VIEW],
      },
      {
        to: '/leads',
        label: 'Khách tiềm năng',
        icon: UserSearch,
        requiresAny: [PERMISSIONS.LEAD_VIEW],
      },
      {
        to: '/kpi',
        label: 'Chỉ tiêu KPI',
        icon: Target,
        requiresAny: [PERMISSIONS.KPI_VIEW],
      },
      {
        to: '/sales-channels',
        label: 'Kênh bán',
        icon: Store,
        requiresAny: [PERMISSIONS.SALES_CHANNEL_VIEW, PERMISSIONS.SALES_CHANNEL_CONFIG],
      },
    ],
  },
  {
    title: 'Kho & Mua hàng',
    items: [
      {
        to: '/products',
        label: 'Sản phẩm',
        icon: Package,
        requiresAny: [PERMISSIONS.PRODUCT_VIEW, PERMISSIONS.PRODUCT_KEYWORD_MANAGE],
      },
      {
        to: '/materials',
        label: 'Vật tư',
        icon: Boxes,
        requiresAny: [PERMISSIONS.MATERIAL_VIEW],
      },
      {
        to: '/warehouse',
        label: 'Quản lý kho',
        icon: Warehouse,
        requiresAny: [PERMISSIONS.WH_MATERIAL_VIEW],
      },
      {
        to: '/btp',
        label: 'Bán thành phẩm',
        icon: Layers,
        requiresAny: [PERMISSIONS.BTP_VIEW],
      },
      {
        to: '/purchases',
        label: 'Nhập hàng',
        icon: PackagePlus,
        requiresAny: [PERMISSIONS.PURCHASE_VIEW],
      },
      {
        to: '/suppliers',
        label: 'Nhà cung cấp',
        icon: Building2,
        requiresAny: [PERMISSIONS.SUPPLIER_VIEW],
      },
    ],
  },
  {
    title: 'Sản xuất',
    items: [
      {
        // Khoán — thợ khai việc, quản lý duyệt, chốt kỳ, cấu hình (migration 20261102_*).
        to: '/piecework',
        label: 'Khoán',
        icon: Hammer,
        requiresAny: PIECEWORK_MODULE_PERMISSIONS,
      },
    ],
  },
  {
    title: 'Tài chính & Báo cáo',
    items: [
      {
        to: '/finance',
        label: 'Thu chi & Công nợ',
        icon: Wallet,
        requiresAny: [PERMISSIONS.FINANCE_VIEW],
      },
      {
        to: '/expenses',
        label: 'Duyệt chi',
        icon: ReceiptText,
        requiresAny: [PERMISSIONS.EXPENSE_VIEW, PERMISSIONS.EXPENSE_REQUEST],
      },
      {
        to: '/reports',
        label: 'Báo cáo',
        icon: BarChart3,
        // Chung danh sách với route /reports (routes.tsx) — lệch nhau là người có
        // quyền thấy menu mà bấm vào bị 403, hoặc ngược lại.
        requiresAny: REPORTS_HUB_PERMISSIONS,
      },
      {
        to: '/pancake',
        label: 'Hiệu suất Sale',
        icon: MessageSquare,
        requiresAny: [PERMISSIONS.PANCAKE_VIEW],
      },
    ],
  },
  {
    title: 'Hệ thống',
    items: [
      // Settings: always shown. The index page itself filters tiles by
      // permission and shows an empty state if the user has nothing to manage —
      // hiding the entry point entirely just confuses users who expect to find
      // a "Cấu hình" link.
      { to: '/settings', label: 'Cấu hình', icon: Settings },
    ],
  },
]

export function Sidebar() {
  const { has, loading } = usePermissions()
  const { collapsed } = useSidebarCollapsed()
  // Đếm đề nghị hạ COD chờ CHÍNH MÌNH (fail-soft = 0: DB chưa có module / lỗi mạng).
  const codInbox = useCodReductionMyTurnCount(!loading)
  const badgeOf = (item: NavItem) => (item.inboxBadge === 'cod-reductions' ? (codInbox.data ?? 0) : 0)

  // Trong khi đang tải permissions, CHỈ hiện các item không gate (Tổng quan,
  // Cấu hình). Trước đây show optimistic hết menu gây hiện sai mục so với quyền
  // thật ở vài frame đầu — bug "menu hiện sai mục" sau khi đăng nhập / reload.
  // Đổi sang ẩn item gated lúc loading: skeleton ngắn (<1s) tốt hơn flash sai
  // quyền và route gate sẽ bắt được nếu user click vào.
  const isVisible = (item: NavItem) => {
    if (!item.requiresAny || item.requiresAny.length === 0) return true
    if (loading) return false
    if (badgeOf(item) > 0) return true
    return item.requiresAny.some(has)
  }

  // Lọc theo quyền rồi bỏ nhóm rỗng (toàn bộ item bị ẩn) để không render
  // tiêu đề nhóm trơ trọi.
  const groups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter(isVisible),
  })).filter((g) => g.items.length > 0)

  return (
    <aside
      className={cn(
        'hidden h-full shrink-0 flex-col border-r border-border bg-surface transition-[width] duration-200 ease-in-out md:flex',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      <Link
        to="/"
        aria-label={`${env.appName} — về Tổng quan`}
        title={collapsed ? env.appName : undefined}
        className={cn(
          'flex h-14 items-center transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
          collapsed ? 'justify-center px-0' : 'gap-2.5 px-5',
        )}
      >
        {/* Logo trên nền trắng (ảnh có canvas trắng) → tile trắng để hợp cả
            light/dark. */}
        <img
          src={env.appLogo}
          alt={env.appName}
          className="size-8 shrink-0 rounded-lg bg-white object-contain shadow-sm"
        />
        {/* Ẩn chữ khi thu gọn (giữ logo làm mốc điều hướng về Tổng quan). */}
        {!collapsed && <span className="text-sm font-semibold text-fg">{env.appName}</span>}
      </Link>

      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-2">
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <div key={group.title ?? '_root'}>
              {/* Tiêu đề nhóm: ẩn khi thu gọn (không đủ chỗ). Thay bằng đường kẻ
                  mảnh để vẫn phân tách nhóm về mặt thị giác. */}
              {group.title &&
                (collapsed ? (
                  <div className="mx-2 mb-1 border-t border-border" aria-hidden />
                ) : (
                  <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-fg-subtle">
                    {group.title}
                  </div>
                ))}
              <ul className="flex flex-col gap-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        // Tooltip native hiện nhãn khi thu gọn (chỉ còn icon).
                        title={collapsed ? item.label : undefined}
                        className={({ isActive }) =>
                          cn(
                            'group/nav flex items-center rounded-lg text-sm font-medium transition-colors',
                            collapsed ? 'justify-center px-0 py-2.5' : 'gap-2.5 px-3 py-2',
                            isActive
                              ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300'
                              : 'text-fg-muted hover:bg-surface-muted hover:text-fg',
                          )
                        }
                      >
                        <span className="relative flex shrink-0">
                          <Icon className="size-4" strokeWidth={2} />
                          {collapsed && badgeOf(item) > 0 && (
                            <span className="absolute -right-1 -top-1 size-2 rounded-full bg-red-500" aria-hidden />
                          )}
                        </span>
                        {!collapsed && item.label}
                        {!collapsed && badgeOf(item) > 0 && (
                          <span
                            className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white"
                            title="Đề nghị đang chờ bạn xử lý"
                          >
                            {badgeOf(item)}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
          {/* Skeleton placeholder cho các item gated đang chờ quyền — tránh
              sidebar trống lúc fetch get_my_permissions. */}
          {loading && (
            <ul className="flex flex-col gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <li key={`skeleton-${i}`} aria-hidden>
                  <div
                    className={cn(
                      'flex items-center rounded-lg py-2',
                      collapsed ? 'justify-center px-0' : 'gap-2.5 px-3',
                    )}
                  >
                    <span className="size-4 shrink-0 animate-pulse rounded bg-surface-muted" />
                    {!collapsed && (
                      <span className="h-3 w-24 animate-pulse rounded bg-surface-muted" />
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </nav>

      {/* Footer chỉ hiện khi mở rộng (thu gọn không đủ chỗ cho dòng chữ). */}
      {!collapsed && (
        <div className="border-t border-border px-5 py-3 text-xs text-fg-subtle">
          Developed by <span className="font-semibold">Bé Bước</span> 😁
        </div>
      )}
    </aside>
  )
}
